import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  shortTime,
  useAssignCourtSlotMutation,
  useClearCourtSlotMutation,
  type ApiClubCourt,
  type ApiMatch,
} from '@/features/competition';
import { ApiError } from '@/shared/api/ApiError';
import { Alert, Button, DateInput, Input, Label, Modal, Select, useToast } from '@/shared/ui';

export interface ScheduleMatchDialogProps {
  open: boolean;
  onClose: () => void;
  /** Mecz do rozplanowania — stan pol startuje z jego slotu (montuj z `key={match.id}`). */
  match: ApiMatch;
  /** Podpis pary, np. „Kowalski vs Nowak" — opis okna. */
  matchLabel: string;
  /** Korty klubu do wyboru. */
  courts: readonly ApiClubCourt[];
}

/**
 * Okno szybkiego planowania meczu z listy „Zaplanowane": data, godzina i kort
 * bez wchodzenia w pelny planner. Kolizje slotow odrzuca backend (409) —
 * pokazujemy jego komunikat. Mecz z pelnym slotem mozna tez zdjac z kalendarza.
 */
export function ScheduleMatchDialog({
  open,
  onClose,
  match,
  matchLabel,
  courts,
}: ScheduleMatchDialogProps) {
  const { t } = useTranslation('common');
  const { showToast } = useToast();

  const [date, setDate] = useState(match.scheduledDate ?? '');
  const [time, setTime] = useState(shortTime(match.scheduledTime) ?? '');
  const [courtId, setCourtId] = useState(match.courtId ?? '');
  const [error, setError] = useState<string | null>(null);

  const assignMutation = useAssignCourtSlotMutation();
  const clearMutation = useClearCourtSlotMutation();
  const pending = assignMutation.isPending || clearMutation.isPending;

  const hasSlot = Boolean(match.courtId && match.scheduledDate && match.scheduledTime);

  function describe(err: unknown): string {
    return err instanceof ApiError ? err.message : t('scheduledMatchesPage.schedule.failed');
  }

  function handleSave() {
    if (!date || !time || !courtId) {
      setError(t('scheduledMatchesPage.schedule.missing'));
      return;
    }
    setError(null);
    assignMutation.mutate(
      { matchId: match.id, payload: { courtId, date, startTime: time } },
      {
        onSuccess: () => {
          showToast({ tone: 'success', description: t('scheduledMatchesPage.schedule.saved') });
          onClose();
        },
        onError: (err) => setError(describe(err)),
      },
    );
  }

  function handleClear() {
    setError(null);
    clearMutation.mutate(match.id, {
      onSuccess: () => {
        showToast({ tone: 'success', description: t('scheduledMatchesPage.schedule.cleared') });
        onClose();
      },
      onError: (err) => setError(describe(err)),
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title={t('scheduledMatchesPage.schedule.title')}
      description={matchLabel}
      footer={
        <>
          {hasSlot && (
            <Button
              variant="ghost"
              className="mr-auto"
              loading={clearMutation.isPending}
              disabled={assignMutation.isPending}
              onClick={handleClear}
            >
              {t('scheduledMatchesPage.schedule.clear')}
            </Button>
          )}
          <Button variant="secondary" disabled={pending} onClick={onClose}>
            {t('scheduledMatchesPage.schedule.cancel')}
          </Button>
          <Button
            loading={assignMutation.isPending}
            disabled={clearMutation.isPending}
            onClick={handleSave}
          >
            {t('scheduledMatchesPage.schedule.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error && <Alert tone="danger">{error}</Alert>}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="schedule-date">{t('scheduledMatchesPage.schedule.date')}</Label>
            <DateInput
              id="schedule-date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="schedule-time">{t('scheduledMatchesPage.schedule.time')}</Label>
            <Input
              id="schedule-time"
              type="time"
              value={time}
              onChange={(event) => setTime(event.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="schedule-court">{t('scheduledMatchesPage.schedule.court')}</Label>
          <Select
            id="schedule-court"
            placeholder={t('scheduledMatchesPage.schedule.courtPlaceholder')}
            value={courtId}
            onChange={(event) => setCourtId(event.target.value)}
          >
            {courts.map((court) => (
              <option key={court.id} value={court.id}>
                {court.name || t('scheduledMatchesPage.court', { number: court.number })}
              </option>
            ))}
          </Select>
        </div>
      </div>
    </Modal>
  );
}
